/**
 * <s-help-search> — Help Center search palette.
 *
 * Wraps the native <dialog id="help-search"> and a JSON index of every
 * category, section and guide. Opens from Invoker Command buttons
 * (`commandfor="help-search" command="show-modal"`, optional `data-q`) and
 * from Ctrl/Cmd + K or "/" anywhere on the page. Filters locally, ranks
 * title matches first, highlights matches, supports ↑ ↓ ↵ and remembers the
 * last searches in localStorage.
 */
const RECENT_KEY = 'the4-help-searches';
const MAX_RESULTS = 40;

const clean = (s) => String(s || '').replace(/\\/g, '').replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();
const fold = (s) => clean(s).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const escapeHtml = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

class SHelpSearch extends HTMLElement {
  #ac;
  #dialog;
  #input;
  #items = [];
  #logos = [];
  #results = [];
  #active = -1;
  #timer = 0;
  #ready = false;
  #loading = null;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#input = this.querySelector('[data-help-input]');
    if (!this.#dialog || !this.#input) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    // CommandEvent does not bubble: listen on the dialog itself.
    this.#dialog.addEventListener('command', (e) => {
      if (e.command !== 'show-modal') return;
      const q = e.source?.dataset?.q;
      requestAnimationFrame(() => this.#prepare(q));
    }, opts);

    document.addEventListener('keydown', (e) => {
      const key = e.key?.toLowerCase();
      const typing = e.target instanceof HTMLElement && (e.target.isContentEditable || /^(input|textarea|select)$/i.test(e.target.tagName));
      if ((key === 'k' && (e.metaKey || e.ctrlKey)) || (key === '/' && !typing && !e.metaKey && !e.ctrlKey)) {
        e.preventDefault();
        this.open();
      }
    }, opts);

    this.#dialog.addEventListener('click', (e) => {
      if (e.target === this.#dialog) this.#dialog.close();
      const term = e.target.closest?.('[data-help-term]');
      if (term) this.#setQuery(term.dataset.helpTerm);
    }, opts);

    this.#dialog.addEventListener('close', () => {
      const q = this.#input.value.trim();
      if (q.length > 1 && this.#results.length) this.#remember(q);
    }, opts);

    this.#input.addEventListener('input', () => {
      clearTimeout(this.#timer);
      this.#timer = setTimeout(() => this.#search(this.#input.value), 60);
    }, opts);

    this.#input.addEventListener('keydown', (e) => this.#onKey(e), opts);

    this.querySelector('[data-help-clear]')?.addEventListener('click', () => this.#setQuery(''), opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  open(q) {
    if (!this.#dialog.open) this.#dialog.showModal();
    this.#prepare(q);
  }

  /**
   * Loads the index once, on first open (SEO 2026-11-03: it used to be inlined
   * on every Help page, ~210 KB). Resolves true when items are ready.
   */
  #load() {
    if (this.#loading) return this.#loading;
    const url = this.dataset.indexUrl;
    if (!url) return Promise.resolve(false);
    this.#loading = fetch(url, { credentials: 'same-origin', signal: this.#ac?.signal })
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error(String(res.status)))))
      .then((text) => {
        const data = JSON.parse(text.trim());
        this.#items = (data.items || []).map((it) => ({ ...it, t: clean(it.t), d: clean(it.d), p: clean(it.p), ft: fold(it.t), fd: fold(it.d), fp: fold(it.p) }));
        this.#logos = data.logos || [];
        this.#ready = true;
        return true;
      })
      .catch(() => {
        this.#loading = null;
        return false;
      });
    return this.#loading;
  }

  #prepare(q) {
    this.#load();
    this.#renderRecent();
    if (typeof q === 'string') this.#setQuery(q);
    else this.#search(this.#input.value);
    this.#input.focus();
    this.#input.select();
  }

  #setQuery(q) {
    this.#input.value = q;
    this.#search(q);
    this.#input.focus();
  }

  #search(raw) {
    const q = fold(raw);
    const idle = this.querySelector('[data-help-idle]');
    const list = this.querySelector('[data-help-results]');
    const none = this.querySelector('[data-help-none]');
    const clear = this.querySelector('[data-help-clear]');
    const status = this.querySelector('[data-help-status]');
    clear?.classList.toggle('hidden', !q);

    if (!q) {
      this.#results = [];
      idle.classList.remove('hidden');
      list.classList.add('hidden');
      list.classList.remove('flex');
      none.classList.add('hidden');
      none.classList.remove('flex');
      this.#input.setAttribute('aria-expanded', 'false');
      status.textContent = '';
      return;
    }

    if (!this.#ready) {
      this.#load().then((ok) => {
        if (ok && fold(this.#input.value) === q) this.#search(this.#input.value);
      });
      return;
    }

    const words = q.split(' ').filter(Boolean);
    const scored = [];
    for (const it of this.#items) {
      let score = 0;
      let ok = true;
      for (const w of words) {
        const inT = it.ft.indexOf(w);
        if (inT > -1) score += inT === 0 ? 60 : /\s/.test(it.ft[inT - 1] || ' ') ? 40 : 25;
        else if (it.fp.includes(w)) score += 12;
        else if (it.fd.includes(w)) score += 8;
        else { ok = false; break; }
      }
      if (!ok) continue;
      if (it.ft === q) score += 100;
      if (it.k === 'c') score += 6;
      if (it.k === 's') score += 3;
      scored.push({ it, score });
    }
    scored.sort((a, b) => b.score - a.score || a.it.t.length - b.it.t.length);
    this.#results = scored.slice(0, MAX_RESULTS).map((s) => s.it);

    idle.classList.add('hidden');
    const has = this.#results.length > 0;
    list.classList.toggle('hidden', !has);
    list.classList.toggle('flex', has);
    none.classList.toggle('hidden', has);
    none.classList.toggle('flex', !has);
    this.#input.setAttribute('aria-expanded', String(has));

    if (!has) {
      const title = this.querySelector('[data-help-none-title]');
      title.textContent = (title.dataset.template || '').replace('[query]', raw.trim());
      status.textContent = title.textContent;
      list.innerHTML = '';
      this.#active = -1;
      return;
    }

    const re = new RegExp(`(${words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
    const mark = (text) => {
      // Highlight on the accent-folded copy so "cai dat" also marks "cài đặt".
      const folded = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      if (folded.length !== text.length) return escapeHtml(text);
      let out = '';
      let last = 0;
      folded.replace(re, (m, _g, i) => {
        out += escapeHtml(text.slice(last, i)) + '<mark>' + escapeHtml(text.slice(i, i + m.length)) + '</mark>';
        last = i + m.length;
        return m;
      });
      return out + escapeHtml(text.slice(last));
    };

    list.innerHTML = this.#results.map((it, i) => `
      <li role="option" id="help-opt-${i}" aria-selected="false">
        <a href="${escapeHtml(it.u)}" class="help-result" data-index="${i}" tabindex="-1">
          ${this.#logos[it.g] ? `<span class="help-result-icon" data-logo aria-hidden="true">${this.#logos[it.g]}</span>` : `<span class="help-result-icon" data-kind="${it.k}" aria-hidden="true"></span>`}
          <span class="min-w-0 flex-1">
            <span class="block truncate text-base">${mark(it.t)}</span>
            ${it.p ? `<span class="mt-0.5 block truncate text-xs text-on-surface-secondary">${escapeHtml(it.p)}</span>` : ''}
          </span>
          <span class="help-result-kind">${escapeHtml(this.dataset[`label${it.k.toUpperCase()}`] || '')}</span>
        </a>
      </li>`).join('');

    status.textContent = (this.dataset.count || '').replace('[count]', String(this.#results.length));
    list.querySelectorAll('a').forEach((a) => {
      a.addEventListener('mousemove', () => this.#setActive(Number(a.dataset.index), false));
      a.addEventListener('click', () => this.#remember(raw.trim()));
    });
    this.#setActive(0, false);
  }

  #setActive(i, scroll = true) {
    const opts = this.querySelectorAll('[data-help-results] [role="option"]');
    if (!opts.length) return;
    this.#active = (i + opts.length) % opts.length;
    opts.forEach((o, n) => o.setAttribute('aria-selected', String(n === this.#active)));
    this.#input.setAttribute('aria-activedescendant', `help-opt-${this.#active}`);
    if (scroll) opts[this.#active].scrollIntoView({ block: 'nearest' });
  }

  #onKey(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); this.#setActive(this.#active + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this.#setActive(this.#active - 1); }
    else if (e.key === 'Enter') {
      const it = this.#results[this.#active];
      if (!it) return;
      e.preventDefault();
      this.#remember(this.#input.value.trim());
      window.location.href = it.u;
    }
  }

  #recent() {
    try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
  }

  #remember(q) {
    if (!q || q.length < 2) return;
    const next = [q, ...this.#recent().filter((r) => r.toLowerCase() !== q.toLowerCase())].slice(0, 6);
    try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* storage unavailable */ }
  }

  #renderRecent() {
    const wrap = this.querySelector('[data-help-recent-wrap]');
    const list = this.querySelector('[data-help-recent]');
    if (!wrap || !list) return;
    const items = this.#recent();
    wrap.classList.toggle('hidden', items.length === 0);
    list.innerHTML = items.map((q) => `<li><button type="button" class="help-chip" data-help-term="${escapeHtml(q)}">${escapeHtml(q)}</button></li>`).join('');
  }
}

customElements.define('s-help-search', SHelpSearch);
